import * as THREE from 'three';
import {
  accentMaterial,
  bodyMaterial,
  createMotes,
  createPedestal,
  glowMaterial,
  PortalScene,
  SceneFactory,
} from './portal-scene';

interface Operator {
  readonly group: THREE.Group;
  readonly phase: number;
  readonly baseY: number;
}

function createOperator(accent: THREE.Color, withHeadset: boolean): THREE.Group {
  const group = new THREE.Group();

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.32, 0.5, 10, 28), bodyMaterial(0x45539f));
  body.position.y = 0.1;
  group.add(body);

  const badge = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.06, 24), accentMaterial(accent, 1.1));
  badge.rotation.x = Math.PI / 2;
  badge.position.set(0, 0.2, 0.3);
  group.add(badge);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.29, 36, 36), bodyMaterial(0x6b77c4));
  head.position.y = 0.76;
  group.add(head);

  if (withHeadset) {
    const band = new THREE.Mesh(new THREE.TorusGeometry(0.33, 0.045, 16, 40, Math.PI), accentMaterial(accent, 0.9));
    band.position.y = 0.76;
    band.rotation.y = Math.PI / 2;
    group.add(band);

    for (const side of [-1, 1]) {
      const pad = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.07, 24), accentMaterial(accent, 0.9));
      pad.rotation.z = Math.PI / 2;
      pad.position.set(side * 0.31, 0.76, 0);
      group.add(pad);
    }

    const mic = new THREE.Mesh(new THREE.CapsuleGeometry(0.025, 0.26, 6, 12), accentMaterial(accent, 0.9));
    mic.position.set(0.24, 0.62, 0.17);
    mic.rotation.set(0.5, 0, 0.75);
    group.add(mic);
  } else {
    const helmet = new THREE.Mesh(
      new THREE.SphereGeometry(0.31, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2),
      accentMaterial(accent, 0.6),
    );
    helmet.position.y = 0.78;
    group.add(helmet);
  }

  return group;
}

/** Operadores: a crew of figures, the lead one wearing a support headset. */
export const createOperadoresScene: SceneFactory = (accent): PortalScene => {
  const root = new THREE.Group();

  const pivot = new THREE.Group();
  root.add(pivot);

  const layout: ReadonlyArray<{ x: number; z: number; scale: number; headset: boolean }> = [
    { x: 0, z: 0.55, scale: 1.08, headset: true },
    { x: -1.02, z: -0.45, scale: 0.84, headset: false },
    { x: 1.02, z: -0.45, scale: 0.84, headset: false },
  ];

  const operators: Operator[] = layout.map((item, index) => {
    const group = createOperator(accent, item.headset);
    group.scale.setScalar(item.scale);
    const baseY = -0.55;
    group.position.set(item.x, baseY, item.z);
    pivot.add(group);
    return { group, phase: index * 1.15, baseY };
  });

  const halo = new THREE.Mesh(new THREE.TorusGeometry(1.25, 0.018, 12, 96), glowMaterial(accent, 0.55));
  halo.rotation.x = Math.PI / 2;
  halo.position.y = -0.95;
  pivot.add(halo);

  const signal = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.02, 10, 72, Math.PI * 1.4), glowMaterial(accent, 0.7));
  signal.position.set(0, 1.35, 0.3);
  signal.rotation.x = Math.PI / 2.6;
  pivot.add(signal);

  const motes = createMotes(accent, 12, 2.0);
  root.add(motes, createPedestal(accent));

  return {
    root,
    update(elapsed, hover) {
      operators.forEach(({ group, phase, baseY }) => {
        group.position.y = baseY + Math.sin(elapsed * 1.5 + phase) * (0.07 + hover * 0.06);
        group.rotation.y = Math.sin(elapsed * 0.6 + phase) * 0.25;
      });

      halo.rotation.z = elapsed * 0.4;
      signal.rotation.z = elapsed * (0.9 + hover * 1.6);
      signal.scale.setScalar(1 + Math.sin(elapsed * 2.1) * 0.08);
      motes.rotation.y = elapsed * 0.13;
    },
  };
};
